import { getModelsForAggregation } from '@app/common/static-data/models';
import { AnalyticsService } from '@app/api/analytics';
import { ModelState } from './types';
import { VariableType } from '../types/dashboard.types';
import type { MarketZoneId } from '@app/common/static-data/energy-markets';
import { MarketAggregatePoint } from '@app/api/analytics/types';

// Utility function to fetch latest runs for models
export async function fetchLatestRuns(
  weightType: string = 'wind_capacity',
  demo?: boolean,
): Promise<Record<string, string | null>> {
  const promises = getModelsForAggregation(weightType).map((model) =>
    AnalyticsService.getLatestRun(model.id, weightType, demo)
      .then((response) => ({ modelId: model.id, latestInitTime: response }))
      .catch(() => ({ modelId: model.id, latestInitTime: null })),
  );

  const responses = await Promise.all(promises);

  const results: Record<string, string | null> = {};
  for (const res of responses) {
    results[res.modelId] = res.latestInitTime;
  }

  return results;
}

// Shared TTL cache for latest runs (across tabs)
type LatestRunsCacheEntry = {
  runs: Record<string, string | null>;
  timestamp: number;
};

const LATEST_RUNS_MEMORY = new Map<string, LatestRunsCacheEntry>();
const LATEST_RUNS_PREFIX = 'ma-latest-runs-v1:';
const LATEST_RUNS_TTL_MS = 10 * 60 * 1000; // 10 minutes
const inflightLatestRuns = new Map<string, Promise<Record<string, string | null>>>();

function makeLatestRunsKey(weightType: string, demo?: boolean) {
  return `${weightType}|${demo ? 'demo' : 'prod'}`;
}

function loadLatestRunsFromLocalStorage(key: string): LatestRunsCacheEntry | null {
  try {
    const raw = localStorage.getItem(LATEST_RUNS_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LatestRunsCacheEntry;
    if (!parsed || typeof parsed.timestamp !== 'number' || !parsed.runs) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveLatestRunsToLocalStorage(key: string, entry: LatestRunsCacheEntry) {
  try {
    localStorage.setItem(LATEST_RUNS_PREFIX + key, JSON.stringify(entry));
  } catch {
    // ignore
  }
}

export async function fetchLatestRunsCached(
  weightType: string = 'wind_capacity',
  demo?: boolean,
): Promise<Record<string, string | null>> {
  const key = makeLatestRunsKey(weightType, demo);
  const now = Date.now();

  // Memory cache
  const mem = LATEST_RUNS_MEMORY.get(key);
  if (mem && now - mem.timestamp <= LATEST_RUNS_TTL_MS) {
    return mem.runs;
  }

  // Local storage cache
  const disk = loadLatestRunsFromLocalStorage(key);
  if (disk && now - disk.timestamp <= LATEST_RUNS_TTL_MS) {
    LATEST_RUNS_MEMORY.set(key, disk);
    return disk.runs;
  }

  // Dedupe in-flight
  const inflight = inflightLatestRuns.get(key);
  if (inflight) return inflight;

  const promise = fetchLatestRuns(weightType, demo)
    .then((runs) => {
      const entry: LatestRunsCacheEntry = { runs, timestamp: Date.now() };
      LATEST_RUNS_MEMORY.set(key, entry);
      saveLatestRunsToLocalStorage(key, entry);
      return runs;
    })
    .finally(() => {
      inflightLatestRuns.delete(key);
    });

  inflightLatestRuns.set(key, promise);
  return promise;
}

// Helper function to fetch market aggregates for a specific model and run
export async function fetchModelRunData(
  modelId: string,
  initTime: string,
  zoneId: MarketZoneId | MarketZoneId[],
  weightType: string,
  maxLeadTimeHours?: number,
  demo?: boolean,
): Promise<MarketAggregatePoint[]> {
  const response = await AnalyticsService.getMarketAggregates(
    new Date(initTime),
    new Date(initTime),
    modelId,
    zoneId,
    weightType,
    maxLeadTimeHours,
    demo,
  );
  return response.metrics;
}

// Helper function to generate cache key for a single model run
// This enables per-run caching for incremental fetching
export function generateRunCacheKey(
  selectedVariable: VariableType,
  selectedZone: MarketZoneId | MarketZoneId[] | null,
  modelId: string,
  runInitTime: string, // ISO string like "2024-12-05T00:00:00.000Z"
  weightType: string,
  maxLeadTimeHours: number,
  demo?: boolean,
): string {
  const zonePart = Array.isArray(selectedZone)
    ? selectedZone.slice().sort().join(',')
    : (selectedZone ?? '');
  const demoPart = `${demo ? 'demo' : 'prod'}`;
  return `${selectedVariable}-${zonePart}-${modelId}-${runInitTime}-${weightType}-${maxLeadTimeHours}-${demoPart}`;
}

// Helper function to generate a signature for the current selection
// Used to detect when the user's selection has changed (not for caching individual runs)
export function generateSelectionSignature(
  selectedVariable: VariableType,
  selectedZone: MarketZoneId | MarketZoneId[] | null,
  selectedModels: ModelState[],
  weightType: string,
  maxLeadTimeHours: number,
  demo?: boolean,
): string {
  const modelData = selectedModels
    .filter((model) => model.isSelected && model.selectedRuns.length > 0)
    .map(
      (model) =>
        `${model.id}:${model.selectedRuns.map((run) => `${run.date}-${run.time}`).join(',')}`,
    )
    .join('|');

  const zonePart = Array.isArray(selectedZone)
    ? selectedZone.slice().sort().join(',')
    : (selectedZone ?? '');
  const demoPart = `${demo ? 'demo' : 'prod'}`;
  return `${selectedVariable}-${zonePart}-${modelData}-${weightType}-${maxLeadTimeHours}-${demoPart}`;
}

// Shared TTL cache for available zones (per weight type)
type ZonesCacheEntry = {
  zones: string[];
  timestamp: number;
};

const ZONES_MEMORY = new Map<string, ZonesCacheEntry>();
const ZONES_PREFIX = 'ma-available-zones-v1:';
const ZONES_TTL_MS = 10 * 60 * 1000; // 10 minutes
const inflightZones = new Map<string, Promise<string[]>>();

function loadZonesFromLocalStorage(weightType: string): ZonesCacheEntry | null {
  try {
    const raw = localStorage.getItem(ZONES_PREFIX + weightType);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ZonesCacheEntry;
    if (!parsed || typeof parsed.timestamp !== 'number' || !Array.isArray(parsed.zones))
      return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveZonesToLocalStorage(weightType: string, entry: ZonesCacheEntry) {
  try {
    localStorage.setItem(ZONES_PREFIX + weightType, JSON.stringify(entry));
  } catch {
    // ignore
  }
}

export async function fetchAvailableZonesCached(
  weightType: 'population' | 'wind_capacity' | 'solar_capacity',
) {
  const now = Date.now();

  const mem = ZONES_MEMORY.get(weightType);
  if (mem && now - mem.timestamp <= ZONES_TTL_MS) {
    return mem.zones;
  }

  const disk = loadZonesFromLocalStorage(weightType);
  if (disk && now - disk.timestamp <= ZONES_TTL_MS) {
    ZONES_MEMORY.set(weightType, disk);
    return disk.zones;
  }

  const inflight = inflightZones.get(weightType);
  if (inflight) return inflight;

  const promise = AnalyticsService.getAvailableZones(weightType)
    .then((zones) => {
      const entry: ZonesCacheEntry = { zones, timestamp: Date.now() };
      ZONES_MEMORY.set(weightType, entry);
      saveZonesToLocalStorage(weightType, entry);
      return zones;
    })
    .finally(() => {
      inflightZones.delete(weightType);
    });

  inflightZones.set(weightType, promise);
  return promise;
}
